import { z } from 'zod';

export const DetailModeSchema = z.enum(['DATE_ONLY', 'SHIFT_DETAIL']);
export const ShiftLabelSchema = z.enum(['DAY', 'EVENING', 'NIGHT', 'CUSTOM']);
export const ShiftTypeSchema = z.enum(['SHIFT', 'CALL']);
export const WorkLocationSchema = z.enum(['REMOTE', 'ONSITE', 'PARTIAL_REMOTE']);
export const AvailabilityStatusSchema = z.enum(['AVAILABLE', 'HELD', 'BOOKED', 'UNAVAILABLE']);
export const GenerationSourceSchema = z.enum(['MANUAL', 'RECURRENCE']);
export const RateOverrideTypeSchema = z.enum(['HOUR', 'SHIFT', 'RVU']).nullable();

const nullableString = z.string().nullable();
const nullableMoney = z
  .union([z.string(), z.number().transform((value) => String(value))])
  .nullable();

export const AvailabilityEntrySchema = z.object({
  id: z.number(),
  availabilityDate: z.string(),
  serviceListingId: z.number().nullable(),
  recurringRuleId: z.number().nullable(),
  detailMode: DetailModeSchema,
  startsAt: nullableString,
  endsAt: nullableString,
  timezone: z.string(),
  shiftLabel: ShiftLabelSchema.nullable(),
  shiftType: ShiftTypeSchema.nullable(),
  workLocation: WorkLocationSchema.nullable(),
  stateCode: nullableString,
  status: AvailabilityStatusSchema,
  rateOverrideType: RateOverrideTypeSchema,
  pricePerHourOverride: nullableMoney,
  pricePerShiftOverride: nullableMoney,
  pricePerCallShiftOverride: nullableMoney,
  notes: nullableString,
  generationSource: GenerationSourceSchema,
  generatedAt: nullableString,
  bookedContractId: z.number().nullable(),
  heldByNegotiationId: z.number().nullable(),
  holdExpiresAt: nullableString,
  createdAt: nullableString,
  updatedAt: nullableString,
});

export const AvailabilityDaySchema = z.object({
  date: z.string(),
  entries: z.array(AvailabilityEntrySchema),
});

/**
 * A contract the provider is party to, shown on its start date (MCV-1821). Read-only: the
 * calendar never writes contracts. Only JOB and SERVICE contracts can involve a provider.
 */
export const CalendarContractSchema = z.object({
  contractId: z.number(),
  listingType: z.enum(['JOB', 'SERVICE']),
  title: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  status: z.string(),
  providerServiceListingId: z.number().nullable(),
});

export const CalendarContractsResponseSchema = z.array(CalendarContractSchema);

export const AvailabilityRangeResponseSchema = z.object({
  start: z.string(),
  end: z.string(),
  days: z.array(AvailabilityDaySchema),
});

export const BulkAvailabilityResponseSchema = z.object({
  updatedDates: z.array(z.string()),
  days: z.array(AvailabilityDaySchema),
});

export const DayMutationResponseSchema = z.object({ day: AvailabilityDaySchema });
export const BatchReplaceDaysResponseSchema = z.object({ days: z.array(AvailabilityDaySchema) });
export const DeleteDayResponseSchema = z.object({
  date: z.string(),
  deletedEntryIds: z.array(z.number()),
});
export const DeleteRuleResponseSchema = z.object({
  ruleId: z.number(),
  deleted: z.literal(true),
});

export const WritableAvailabilityEntrySchema = z.object({
  // Echoed back from a range read so the API can keep a recurrence-generated
  // occurrence linked to its rule and record a typed override. Absent = create.
  id: z.number().optional(),
  serviceListingId: z.number().nullable().optional(),
  detailMode: DetailModeSchema,
  timezone: z.string().optional(),
  startsAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
  shiftLabel: ShiftLabelSchema.nullable().optional(),
  shiftType: ShiftTypeSchema.nullable().optional(),
  workLocation: WorkLocationSchema.nullable().optional(),
  stateCode: z.string().nullable().optional(),
  status: z.enum(['AVAILABLE', 'UNAVAILABLE']).optional(),
  rateOverrideType: RateOverrideTypeSchema.optional(),
  pricePerHourOverride: nullableMoney.optional(),
  pricePerShiftOverride: nullableMoney.optional(),
  pricePerCallShiftOverride: nullableMoney.optional(),
  notes: z.string().nullable().optional(),
});

export const AvailabilityRuleSchema = z.object({
  id: z.number(),
  serviceListingId: z.number().nullable(),
  effectiveStartDate: z.string(),
  effectiveEndDate: nullableString,
  detailMode: DetailModeSchema,
  timezone: z.string(),
  daysOfWeekMask: z.number().int(),
  startTime: nullableString,
  endTime: nullableString,
  shiftLabel: ShiftLabelSchema.nullable(),
  shiftType: ShiftTypeSchema.nullable(),
  workLocation: WorkLocationSchema.nullable(),
  stateCode: nullableString,
  rateOverrideType: RateOverrideTypeSchema,
  pricePerHourOverride: nullableMoney,
  pricePerShiftOverride: nullableMoney,
  pricePerCallShiftOverride: nullableMoney,
  notes: nullableString,
  createdAt: nullableString,
  updatedAt: nullableString,
});

export const AvailabilityRulesResponseSchema = z.object({
  rules: z.array(AvailabilityRuleSchema),
});

export const WritableAvailabilityRuleSchema = AvailabilityRuleSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({ requestTimezone: z.string().optional() });

// PATCH requests deliberately omit unchanged fields. In particular, an active
// rule commonly has an effectiveStartDate in the past; resending that unchanged
// date would make the API treat it as a new historical mutation. The persisted
// schedule timezone stays required; requestTimezone separately carries the
// provider's current browser zone for past-date policy while traveling.
export const WritableAvailabilityRulePatchSchema = WritableAvailabilityRuleSchema.partial().extend({
  timezone: z.string(),
  requestTimezone: z.string().optional(),
});

export const AvailabilityExceptionSchema = z.object({
  id: z.number(),
  recurringRuleId: z.number(),
  serviceListingId: z.number().nullable(),
  exceptionDate: z.string(),
  exceptionType: z.enum(['CANCEL', 'TIME_OVERRIDE', 'SHIFT_OVERRIDE', 'RATE_OVERRIDE']),
  overrideStartTime: nullableString,
  overrideEndTime: nullableString,
  overrideShiftLabel: ShiftLabelSchema.nullable(),
  overrideShiftType: ShiftTypeSchema.nullable(),
  rateOverrideType: RateOverrideTypeSchema,
  pricePerHourOverride: nullableMoney,
  pricePerShiftOverride: nullableMoney,
  pricePerCallShiftOverride: nullableMoney,
  notes: nullableString,
  createdAt: nullableString,
  updatedAt: nullableString,
});

export const WritableAvailabilityExceptionSchema = AvailabilityExceptionSchema.omit({
  id: true,
  recurringRuleId: true,
  serviceListingId: true,
  createdAt: true,
  updatedAt: true,
})
  .partial({
    overrideStartTime: true,
    overrideEndTime: true,
    overrideShiftLabel: true,
    overrideShiftType: true,
    rateOverrideType: true,
    pricePerHourOverride: true,
    pricePerShiftOverride: true,
    pricePerCallShiftOverride: true,
    notes: true,
  })
  .extend({
    timezone: z.string(),
    requestTimezone: z.string().optional(),
  });

export const ProviderServiceListingSummarySchema = z
  .object({
    id: z.number(),
    listingTitle: z.string(),
    status: z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED']),
  })
  .passthrough();

export const AvailabilityConflictBodySchema = z.object({
  detail: z.string(),
  extra: z.object({
    code: z.literal('AVAILABILITY_CONFLICT'),
    entryIds: z.array(z.number()).default([]),
    bookedContractIds: z.array(z.number()).default([]),
    heldByNegotiationIds: z.array(z.number()).default([]),
  }),
});
